'use client';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import EffortRating from '@components/shared/effort-rating.jsx';
import { ToutContent, ToutContentButtons } from '@repo/ui/src/components/ToutContent';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { useIsSM, useIsLG } from '@repo/ui/src/utils/useMediaQuery';
import { ButtonStyleEnum, ButtonIconEnum } from '@enums/button-enums.js';
import { AuthorProps, RecipeMetaSectionProps, MetaProps } from '@repo/ui/src/types';
import { CurrentUrlProps } from '@repo/ui/src/types/currentUrl';

import {
  EffortLabel,
  DividerVertical,
  Label,
  NumberContainer,
  Summary,
  BackgroundImage,
  AuthorImage,
  Content,
  Image,
  StyledGrid,
} from './styles';

interface RecipeCardProps {
  _meta: MetaProps;
  assets?: Array<{
    endpoint: string;
    name: string;
  }>;
  author?: AuthorProps;
  recipeMetaSection?: RecipeMetaSectionProps;
  heading?: string;
  link?: string;
  linkLabel?: string;
  by?: string;
  prepTimeLabel?: string;
  cookTimeLabel?: string;
  effort?: string;
  outOf?: string;
  currentUrl?: CurrentUrlProps;
  _testID?: string;
  index?: number;
}

const RecipeCard = ({
  _meta,
  assets,
  author,
  recipeMetaSection,
  heading,
  link,
  linkLabel,
  by,
  prepTimeLabel,
  cookTimeLabel,
  effort,
  outOf,
  _testID,
  index,
}: RecipeCardProps) => {
  const lgAndUp = useIsLG();
  const smAndUp = useIsSM();
  const buttons = [
    {
      link: { link: _meta.deliveryKey, label: linkLabel, displayName: link },
      style: ButtonStyleEnum.Text,
      icon: ButtonIconEnum.Skewer,
    },
  ];
  const imgUrl = buildImageSrc(
    assets?.length ? assets[0] : null,
    smAndUp ? 700 : 600,
    smAndUp ? 9 : 1,
    smAndUp ? 10 : 1,
  );
  const authorImgUrl = buildImageSrc(author?.avatar, 37, 1, 1);
  const logBackground = {
    endpoint: 'traeger',
    name: 'log-cropped-transparent',
  };
  const logBackgroundImgUrl = buildImageSrc(logBackground, 500, 0, 0);
  if (!recipeMetaSection) return null;

  return (
    <Grid
      container
      direction={smAndUp ? 'row-reverse' : 'row'}
      data-test-id={`recipe_card_${_testID}${index}`}
      className="tw-h-full"
    >
      <Grid item xs={12} sm={6}>
        {/* eslint-disable-next-line jsx-a11y/alt-text,@next/next/no-img-element */}
        <Image imgComponent={<img src={imgUrl} alt={recipeMetaSection.title} />} />
      </Grid>
      <StyledGrid item xs={12} sm={6}>
        <BackgroundImage
          imgComponent={<img src={logBackgroundImgUrl} role="presentation" alt="" />}
        />

        <Content>
          {heading && (
            <ToutContent
              align="left"
              heading={recipeMetaSection.title}
              headingStyleVariant="h2"
              lilHeading={heading}
              className="tw-pb-6"
            />
          )}
          {author && (
            <Box display="flex" alignItems="center" mb={2}>
              <AuthorImage imgComponent={<img src={authorImgUrl} alt={author.name} />} />

              <Typography variant="body1">
                {by} {author.name}
              </Typography>
            </Box>
          )}
          <ToutContent
            align="left"
            lineClamp={4}
            description={recipeMetaSection.description}
            className="tw-pb-6"
          />
          <Summary
            mt={3}
            display="flex"
            alignItems="center"
            justifyContent={lgAndUp ? 'flex-start' : 'space-between'}
          >
            <Box display="flex" alignItems="center">
              <NumberContainer>{recipeMetaSection.prepTime}</NumberContainer>
              <Label>{prepTimeLabel}</Label>
            </Box>
            <DividerVertical />
            <Box display="flex" alignItems="center">
              <NumberContainer>{recipeMetaSection.cookTime}</NumberContainer>
              <Label>{cookTimeLabel}</Label>
            </Box>
            {smAndUp && <DividerVertical />}
            <Box>
              <EffortLabel display="flex" alignItems="center">
                {effort && <span>{`${effort}:`}</span>}
                <EffortRating
                  complexity={recipeMetaSection.complexity}
                  iconWidth="16px"
                  effortText={effort}
                  outOfText={outOf}
                />
              </EffortLabel>
            </Box>
          </Summary>
          <ToutContentButtons className="tw-mt-1" buttons={buttons} align="left" />
        </Content>
      </StyledGrid>
    </Grid>
  );
};

export default RecipeCard;
